import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useProduct } from "@/lib/product-context";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Rocket } from "lucide-react";

export default function NewSessionPage() {
  const navigate = useNavigate();
  const { meta } = useProduct();
  const [form, setForm] = useState({
    tester_name: "",
    browser: "",
    device: "",
    home_url: "",
    app_url: "",
    environment: meta?.environment || "UAT",
  });
  const [submitting, setSubmitting] = useState(false);

  // Load defaults from the current product's checklist
  useEffect(() => {
    (async () => {
      try {
        const cl = await api.getChecklist();
        setForm((f) => ({
          ...f,
          home_url: f.home_url || cl.home_url || "",
          app_url: f.app_url || cl.app_url || "",
          environment: cl.environment || f.environment || "UAT",
        }));
      } catch { /* silent */ }
    })();
  }, [meta?.key]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.tester_name.trim()) {
      toast.error("Please enter a tester name");
      return;
    }
    try {
      setSubmitting(true);
      const session = await api.createSession(form);
      toast.success("Session started");
      navigate(`/session/${session.id}/run`);
    } catch (err) {
      toast.error("Failed to create session");
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-8">
        <div className="overline mb-2">Start a session</div>
        <h1 className="font-heading text-3xl md:text-4xl font-bold tracking-tight">
          New UAT run
        </h1>
        <p className="mt-2 text-slate-600 text-sm">
          Enter your tester details. Each session is tracked separately so multiple
          testers can run the checklist in parallel.
        </p>
      </div>

      <form
        onSubmit={onSubmit}
        data-testid="new-session-form"
        className="bg-white border border-slate-200 p-6 md:p-8 space-y-5"
      >
        <FormField label="Tester name" required>
          <Input
            data-testid="input-tester-name"
            value={form.tester_name}
            onChange={update("tester_name")}
            placeholder="Alex Rivera"
            className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
          />
        </FormField>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label="Browser">
            <Input
              data-testid="input-browser"
              value={form.browser}
              onChange={update("browser")}
              placeholder="Chrome 131"
              className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
            />
          </FormField>
          <FormField label="Device">
            <Input
              data-testid="input-device"
              value={form.device}
              onChange={update("device")}
              placeholder="MacBook Pro 14"
              className="rounded-none border-slate-300 focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <FormField label="Home URL">
            <Input
              data-testid="input-home-url"
              value={form.home_url}
              onChange={update("home_url")}
              className="rounded-none border-slate-300 font-mono text-sm focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
            />
          </FormField>
          <FormField label="App URL">
            <Input
              data-testid="input-app-url"
              value={form.app_url}
              onChange={update("app_url")}
              className="rounded-none border-slate-300 font-mono text-sm focus-visible:ring-1 focus-visible:ring-blue-500 focus-visible:border-blue-500"
            />
          </FormField>
        </div>

        <div className="pt-4 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between border-t border-slate-100">
          <div className="text-xs text-slate-500">
            {meta?.name || "Klystic"} ·{" "}
            <span className="font-mono text-slate-900">{form.environment || "UAT"}</span>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="start-session-submit"
            className={`${meta?.key === "tarklen" ? "bg-tarklen-blue hover:bg-tarklen-blue-dark" : "bg-keplerio-navy hover:bg-keplerio-navy-dark"} text-white rounded-none h-11 px-6`}
          >
            <Rocket size={16} className="mr-2" />
            {submitting ? "Starting…" : "Start session"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function FormField({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="overline text-slate-500">
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </Label>
      {children}
    </div>
  );
}
